const initialState = {
  // Идентификатор активной главы обучения (см. src/configs/tutorial) или null
  chapterId: null,
  // Глава уже была пройдена до этого прохождения (признак метрик: первое
  // прохождение или повторное) — фиксируется на старте главы
  isRepeatRun: false,
  // Индекс текущего шага сценария в chapter.steps
  stepIndex: 0,
  // Идентификатор открытой обучающей модалки (см. tutorialModalsRegistry)
  activeModalId: null,
  // Для шага с модалкой: модалка этого шага уже показана (чтобы не
  // переоткрывать после закрытия, пока шаг не пройден)
  isStepModalShown: false,
  // Последний шаг пройден — показывается поздравление главы
  isChapterCompleted: false,
  // Результат сохранения прогресса на сервере (для повторной попытки/лога)
  isCompletionSaved: false,
  // Отряды, которые игрок выбирал на ТЕКУЩЕМ шаге (сбрасывается при
  // продвижении) — для шагов «изучи расчет урона каждого отряда»
  // (предикат unitsInspected, стрелки на еще не просмотренные отряды)
  inspectedUnitIds: [],
  // Ячейки, которые игрок инспектировал на ТЕКУЩЕМ шаге (клик по ячейке ->
  // SET_INSPECTED_HEX -> панель описания) — предикат hexInspected
  inspectedHexKeys: [],
  // Источники, из которых игрок открывал карточку фракции на ТЕКУЩЕМ шаге
  // ('players_panel' | 'complete_button' | 'acting_badge', CARD_OPEN_SOURCES), и открыта ли карточка сейчас —
  // для предиката factionCardViewedFrom
  factionCardOpenSources: [],
  isFactionCardOverlayOpen: false,
  // Разделы правил, страницу которых игрок пролистал на ТЕКУЩЕМ шаге
  // (предикат rulesSectionScrolled) — по разделам, чтобы прокрутка одной
  // страницы не засчитывала задание про другую
  scrolledRulesSectionIds: [],
  // То же для карточки политики (предикат policyCardViewedFrom)
  policyCardOpenSources: [],
  isPolicyCardOverlayOpen: false,
  // Шаг главы по записи прохождения (только при ее просмотре): записанные
  // экшены сценария несут шаг так, как он был в конфиге главы на момент
  // прохождения, — конфиги с тех пор могли поменяться
  replayMeta: null,
  // Какая боковая панель сверху (зеркало SideMenuStackContext) — для шагов
  // «переключись на панель списка игроков» (предикат sideMenuOnTop).
  // Состояние UI, а не накопитель шага — на TUTORIAL_ADVANCE_STEP не
  // сбрасывается
  topSideMenuId: null,
};

// Прогресс сценария активной главы обучения. Живет в private-стейте: серверу
// и другим игрокам он не нужен (все экшены noServerSync), а RESET_STATE при
// выходе в меню сбрасывает его автоматически — ровно согласованное поведение
// «выход из главы = прогресс главы теряется»
const tutorialScenarioReducer = (state = initialState, action) => {
  switch (action.type) {
    case 'TUTORIAL_START_CHAPTER':
      return {
        ...initialState,
        chapterId: action.payload.chapterId,
        isRepeatRun: Boolean(action.payload.isRepeatRun),
      };

    case 'TUTORIAL_RESET':
      return initialState;

    case 'TUTORIAL_ADVANCE_STEP':
      return {
        ...state,
        stepIndex: state.stepIndex + 1,
        isStepModalShown: false,
        inspectedUnitIds: [],
        inspectedHexKeys: [],
        factionCardOpenSources: [],
        policyCardOpenSources: [],
        scrolledRulesSectionIds: [],
      };

    // Открытие/закрытие карточки фракции: источники копятся в рамках шага
    case 'FACTION_CARD_OVERLAY_TOGGLED': {
      if (!state.chapterId || state.isChapterCompleted) {
        return state;
      }
      const {isOpen, source} = action.payload || {};
      const factionCardOpenSources = isOpen && source && !state.factionCardOpenSources.includes(source)
        ? [...state.factionCardOpenSources, source]
        : state.factionCardOpenSources;
      return {
        ...state,
        isFactionCardOverlayOpen: Boolean(isOpen),
        factionCardOpenSources,
      };
    }

    // Аналогично — карточка политики
    case 'POLICY_CARD_OVERLAY_TOGGLED': {
      if (!state.chapterId || state.isChapterCompleted) {
        return state;
      }
      const {isOpen, source} = action.payload || {};
      const policyCardOpenSources = isOpen && source && !state.policyCardOpenSources.includes(source)
        ? [...state.policyCardOpenSources, source]
        : state.policyCardOpenSources;
      return {
        ...state,
        isPolicyCardOverlayOpen: Boolean(isOpen),
        policyCardOpenSources,
      };
    }

    case 'SIDE_MENU_BROUGHT_TO_FRONT': {
      if (!state.chapterId || state.isChapterCompleted) {
        return state;
      }
      return {
        ...state,
        topSideMenuId: action.payload?.sideMenuId || null,
      };
    }

    // Прокрутка страницы раздела правил вниз копится в рамках текущего шага —
    // предикат rulesSectionScrolled («пролистай страницу»). Диспатчит только
    // подсказка внутри открытых правил (TutorialRulesScrollHint)
    case 'RULES_SECTION_SCROLLED': {
      if (!state.chapterId || state.isChapterCompleted) {
        return state;
      }
      const sectionId = action.payload?.sectionId;
      if (!sectionId || state.scrolledRulesSectionIds.includes(sectionId)) {
        return state;
      }
      return {
        ...state,
        scrolledRulesSectionIds: [...state.scrolledRulesSectionIds, sectionId],
      };
    }

    // Инспекция ячейки (клик по ячейке вне подсветки действий) копится в
    // рамках текущего шага — предикат hexInspected («посмотри ячейку храма»)
    case 'SET_INSPECTED_HEX': {
      if (!state.chapterId || state.isChapterCompleted) {
        return state;
      }
      const hexKey = action.payload?.hexKey;
      if (!hexKey || state.inspectedHexKeys.includes(hexKey)) {
        return state;
      }
      return {
        ...state,
        inspectedHexKeys: [...state.inspectedHexKeys, hexKey],
      };
    }

    // Выбор отряда игроком (клик по отряду) копится в рамках текущего шага —
    // предикат unitsInspected «изучил расчет урона каждого участника боя»
    case 'SELECT_UNIT': {
      // Выбор автовыбором (autoSelectUnit) — не действие игрока
      if (!state.chapterId || state.isChapterCompleted || action.meta?.auto) {
        return state;
      }
      const unitId = action.payload?.unitId;
      if (!unitId || state.inspectedUnitIds.includes(unitId)) {
        return state;
      }
      return {
        ...state,
        inspectedUnitIds: [...state.inspectedUnitIds, unitId],
      };
    }

    case 'TUTORIAL_SET_MODAL':
      return {
        ...state,
        activeModalId: action.payload.modalId,
        isStepModalShown: true,
      };

    case 'TUTORIAL_CLOSE_MODAL':
      return {
        ...state,
        activeModalId: null,
      };

    case 'TUTORIAL_CHAPTER_COMPLETED':
      return {
        ...state,
        isChapterCompleted: true,
        activeModalId: null,
      };

    case 'TUTORIAL_COMPLETION_SAVED':
      return {
        ...state,
        isCompletionSaved: true,
      };

    default:
      return state;
  }
};

const TUTORIAL_SCENARIO_ACTION_TYPES = new Set([
  'TUTORIAL_START_CHAPTER',
  'TUTORIAL_ADVANCE_STEP',
  'TUTORIAL_SET_MODAL',
  'TUTORIAL_CLOSE_MODAL',
  'TUTORIAL_CHAPTER_COMPLETED',
]);

const tutorialReducer = (state = initialState, action) => {
  const nextState = tutorialScenarioReducer(state, action);
  if (action.tutorialMeta && TUTORIAL_SCENARIO_ACTION_TYPES.has(action.type)) {
    return {...nextState, replayMeta: action.tutorialMeta};
  }
  return nextState;
};

export default tutorialReducer;
